import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Inbox, X, RefreshCw } from "lucide-react";
import { fetchSubmissions } from "@/lib/api";

const TYPE_LABELS = {
  invite: "Invitation",
  interest: "Warfare Interest",
  associate: "Associate",
  coaching: "Coaching",
  wellness: "Wellness",
  contact: "Contact",
  warfare_registration: "Warfare Registration",
};

export default function SubmissionsDrawer() {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchSubmissions();
      setSubs(r.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (!open) load();
    setOpen(!open);
  };

  return (
    <>
      <button data-testid="view-submissions-btn" onClick={toggle} aria-label="View submissions"
        className="group fixed bottom-6 right-6 z-50 p-4 rounded-full bg-gradient-to-br from-amber-400 to-violet-500 text-white shadow-[0_10px_35px_-8px_rgba(139,92,246,0.7)] hover:scale-110 hover:shadow-[0_10px_45px_-6px_rgba(245,158,11,0.7)] transition-all duration-300">
        <span aria-hidden className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400 to-violet-500 opacity-40 animate-ping group-hover:animate-none" />
        <Inbox size={20} className="relative" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.aside data-testid="submissions-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[60] w-full sm:w-[420px] bg-[#0B0E16]/95 backdrop-blur-xl border-l border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div>
                <p className="font-display text-xl font-bold text-white">Submissions</p>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-vg">{subs.length} received</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="submissions-refresh-btn" onClick={load} className="p-2 text-slate-400 hover:text-white transition-colors" aria-label="Refresh">
                  <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
                </button>
                <button data-testid="submissions-close-btn" onClick={toggle} className="p-2 text-slate-400 hover:text-white transition-colors" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 space-y-4">
              {!loading && subs.length === 0 && (
                <p data-testid="submissions-empty" className="text-sm text-slate-500">No submissions yet. They will appear here as forms are submitted.</p>
              )}
              {subs.map((s) => (
                <div key={s.id} data-testid={`submission-item-${s.id}`} className="rounded-xl glass p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-400/15 to-violet-500/15 border border-white/15 text-slate-200 font-mono text-[11px] font-semibold uppercase tracking-[0.2em]">
                      {TYPE_LABELS[s.type] || s.type}
                    </span>
                    <span className="text-xs text-slate-400">{new Date(s.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-3 text-base font-semibold text-slate-100">{s.name}</p>
                  <p className="text-sm text-slate-300">{s.email}{s.phone ? ` · ${s.phone}` : ""}</p>
                  {s.organization && <p className="mt-1 text-sm text-slate-400">{s.organization}</p>}
                  {s.message && <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s.message}</p>}
                </div>
              ))}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
